import { Injectable } from "@angular/core";
import { PreloadingStrategy, Route } from "@angular/router";
import { Observable, catchError, of, switchMap, timer } from "rxjs";
import { AppRoutes } from "../../enums";
import { ENTRY_ROUTE_CHUNKS } from "../../utils/entry-route-prefetch/entry-route-prefetch";

/** Lazy routes the app opens on its own: its top-level pages and the sections of the admin shell. */
const SHELL_ROUTES = new Set<string>(["", AppRoutes.notFound, AppRoutes.onboarding, ...Object.keys(ENTRY_ROUTE_CHUNKS)]);

export const PAGE_PRELOAD_DELAY = 1000;

const whenIdle = (): Observable<void> =>
  new Observable<void>(subscriber => {
    const done = (): void => {
      subscriber.next();
      subscriber.complete();
    };
    if (typeof window.requestIdleCallback !== "function") return done();
    const handle = window.requestIdleCallback(done, { timeout: PAGE_PRELOAD_DELAY });
    return () => window.cancelIdleCallback?.(handle);
  });

/**
 * Downloads the pages inside the sections the merchant has opened (editors, detail pages) once the page is idle.
 * Opening one then takes no download: the shell's page tabs give way to it right after the click, inside the 500 ms
 * that CLS leaves to input, instead of when its chunk arrives (Built for Shopify 2.1). The router asks again after
 * every navigation, so only sections visited get their pages; the sections themselves load on their first visit.
 */
@Injectable({ providedIn: "root" })
export class SectionPagesPreloadingStrategy implements PreloadingStrategy {
  public preload(route: Route, load: () => Observable<unknown>): Observable<unknown> {
    const path = route.path ?? "";
    if (SHELL_ROUTES.has(path) || path.includes("/")) return of(null);

    return timer(PAGE_PRELOAD_DELAY).pipe(
      switchMap(() => whenIdle()),
      switchMap(() => load()),
      catchError(() => of(null)),
    );
  }
}
